<template>
  <v-row class="DatePicker ma-0">
    <v-col
      class="pa-0"
      cols="12"
    >
      <div
        v-if="label !== ''"
        class="datepicker-input-label p p-bold app-color-1"
        :class="{
          required: required,
        }"
      >
        {{ label }}
      </div>
      <v-menu
        ref="menu"
        v-model="menu"
        :close-on-content-click="false"
        transition="scale-transition"
        :attach="datePickerContainerNode"
        offset-y
        min-width="auto"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            ref="textfield"
            v-model="dateFormatted"
            :label="label"
            :hint="hint"
            :rules="rules"
            autocomplete="off"
            persistent-hint
            :hide-details="hideDetails"
            prepend-inner-icon="calendar_today"
            v-bind="attrs"
            full-width
            outlined
            :placeholder="placeholder !== '' ? placeholder : label !== '' ? label : '...'"
            @change="saveDate(dateFormatted)"
            v-on="on"
            @click:prepend-inner="menu = true"
          />
        </template>
        <v-date-picker
          v-model="datePicked"
          no-title
          scrollable
          :color="color"
          :locale="locale"
          :min="min"
          :max="max"
        />
      </v-menu>
    </v-col>
    <v-col
      ref="datePickerContainer"
      class="pa-0"
      cols="12"
    />
  </v-row>
</template>

<script type="text/javascript">
  import mixinUi from '@/mixins/utils/ui.js'
  import mixinStrFormat from '@/mixins/utils/strFormat.js'
  import getColor from '@/utils/getColor'
  import { TEXT_INPUT_COLOR } from '@/components/ui/inputs/TextInput.const'
  import { autocompleteDate, parseDate } from './DatePicker.utils'

  export default {
    mixins: [mixinUi, mixinStrFormat,],
    model: {
      prop: 'datePickedProp',
      event: 'input',
    },
    props: {
      label: {
        type: String,
        default: '',
      },
      min: {
        type: String,
        default: '',
      },
      max: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        default: '',
      },
      hint: {
        type: String,
        default: '',
      },
      locale: {
        type: String,
        default: 'fr',
      },
      color: {
        type: String,
        default: getColor('_009ee0'),
      },
      datePickedProp: {
        type: String,
        default: null,
      },
      required: {
        type: Boolean,
        default: false,
      },
      rules: {
        type: Array,
        default: () => [],
      },
      hideDetails: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      TEXT_INPUT_COLOR,
      datePicked: null,
      dateFormatted: null,
      menu: false,
      datePickerContainerNode: null,
      isLoaded: false,
    }),
    watch: {
      datePicked() {
        this.dateFormatted = this.formatDate(this.datePicked)
        if (this.isLoaded) {
          this.isLoaded = false

          return
        }
        if (!this.dateFormatted && this.datePicked) {
          this.datePicked = null

          return
        }
        this.$emit('input', this.datePicked)
      },
      dateFormatted(date, oldDate) {
        if (!date) {
          return null
        }
        const dateWithoutLetters = date.replace(/[^0-9]/g, '')
        // do not autocomplete if user remove trailling /
        if (oldDate && oldDate.endsWith('/') && date.length === oldDate.length - 1) {
          return
        }
        this.$nextTick(() => {
          this.dateFormatted = autocompleteDate(dateWithoutLetters)
        })
      },
    },
    created() {
      if (this.datePickedProp) {
        this.isLoaded = true
      }
      this.datePicked = this.datePickedProp
    },
    mounted() {
      this.datePickerContainerNode = this.$refs.datePickerContainer
    },
    methods: {
      formatDate(date) {
        if (!isNaN(Date.parse(date))) {
          return this.$d(new Date(date))
        }

        return null
      },
      saveDate(date) {
        this.datePicked = parseDate(date)
      },
    },
  }
</script>

<style lang="scss">
/* stylelint-disable max-nesting-depth */
.DatePicker {
  .datepicker-input-label {
    margin-left: 8px;
    margin-bottom: 4px;
    &.required::after {
      content: '*';
    }
  }

  .v-input__control {
    .v-input__slot {
      margin-bottom: 0;
      min-height: 52px;

      .v-text-field__slot {
        max-height: 48px;

        label {
          top: 14px;
        }

        input {
          color: $color-004f9f;
          font-size: 16px;
          letter-spacing: 0.24px;
          line-height: 24px;
        }
      }

      .v-input__prepend-inner {
        background-color: $color-f3f9ff;
        margin: 0 16px 0 -12px;
        padding: 0;
        width: 48px;
        height: 48px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-right: 1.4px solid $color-d5e4f3;
        border-radius: 8px 0 0 8px;

        .v-icon {
          color: $color-001c4b;
        }
      }

      fieldset {
        max-height: 53px;
        border: 1.4px solid $color-d5e4f3;
        border-radius: 8px;
      }
    }
  }
  .v-input.error--text {
    .v-input__control {
      .v-input__slot {
        .v-input__prepend-inner {
          height: 48px;
          margin: 0 16px 0 -12px;
          padding: 0;
          left: -11px;
          width: 48px;
          border: 1.4px solid $color-ff524f;
          border-radius: 8px 0 0 8px;
          background-color: $color-255_82_79_16;
          text-align: center;

          .v-input__icon {
            i {
              font-size: 24px;
              color: $color-ff524f !important;
            }
          }
        }

        fieldset {
          max-height: 53px;
          border: 1.4px solid $color-ff524f !important;
          border-radius: 8px;
        }
        .v-select__slot {
          max-height: 48px;
          label {
            left: 5px !important;
            right: auto !important;
            top: auto !important;
            position: absolute;
          }
          input {
            padding-top: 0;
            padding-bottom: 0;
            padding-left: 8px;
            padding-right: 8px;
            color: $color-ff524f;
            font-size: 16px;
            letter-spacing: 0.24px;
            line-height: 24px;
          }
        }
      }
    }
  }
}
</style>
